import React, { useCallback, useState } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { formatDateTime } from '../../utils/datetime';
import type { PosNote } from '../../types';

interface TaskNoteThreadProps {
  posId: string;
  visitId: string;
  legacyNotes?: string;
}

function useTaskNotes(posId: string, visitId: string) {
  const [notes, setNotes] = useState<PosNote[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadNotes = useCallback(async () => {
    if (loaded || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get<PosNote[]>(`/pos/${posId}/notes`, {
        params: { visit_id: visitId },
      });
      setNotes(Array.isArray(data) ? data : []);
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
      setNotes([]);
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  }, [loaded, loading, posId, visitId]);

  const appendNote = useCallback((note: PosNote) => {
    setNotes((prev) => [...prev, note]);
  }, []);

  return { notes, loaded, loadFailed, loading, loadNotes, appendNote };
}

export const TaskNoteThread: React.FC<TaskNoteThreadProps> = ({
  posId,
  visitId,
  legacyNotes,
}) => {
  const { isOnline } = useOnlineStatus();
  const { notes, loaded, loadFailed, loading, loadNotes, appendNote } =
    useTaskNotes(posId, visitId);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleExpand() {
    const next = !expanded;
    setExpanded(next);
    if (next) await loadNotes();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !isOnline || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post<PosNote>(`/pos/${posId}/notes`, {
        body,
        kind: 'task',
        visit_id: visitId,
      });
      appendNote(data);
      setDraft('');
    } catch {
      toast.error('Notiz konnte nicht gespeichert werden');
    } finally {
      setSubmitting(false);
    }
  }

  const summary = notes[0]?.body ?? legacyNotes;
  const showLegacy =
    (!loaded || notes.length === 0 || loadFailed) && !!legacyNotes;

  return (
    <ThreadShell
      expanded={expanded}
      summary={summary}
      onToggle={handleExpand}
      loading={loading}
      notes={notes}
      showLegacy={showLegacy}
      legacyNotes={legacyNotes}
      draft={draft}
      onDraftChange={setDraft}
      onSubmit={handleSubmit}
      isOnline={isOnline}
      submitting={submitting}
    />
  );
};

function ThreadShell({
  expanded,
  summary,
  onToggle,
  loading,
  notes,
  showLegacy,
  legacyNotes,
  draft,
  onDraftChange,
  onSubmit,
  isOnline,
  submitting,
}: {
  expanded: boolean;
  summary?: string;
  onToggle: () => void;
  loading: boolean;
  notes: PosNote[];
  showLegacy: boolean;
  legacyNotes?: string;
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isOnline: boolean;
  submitting: boolean;
}) {
  return (
    <div className="mb-4 rounded-xl border border-purple-100 bg-purple-50/60 overflow-hidden">
      <SummaryButton
        expanded={expanded}
        label={summary ? truncate(summary, 100) : 'Notizen'}
        onClick={onToggle}
      />
      {expanded && (
        <ExpandedBody
          loading={loading}
          notes={notes}
          showLegacy={showLegacy}
          legacyNotes={legacyNotes}
          draft={draft}
          onDraftChange={onDraftChange}
          onSubmit={onSubmit}
          isOnline={isOnline}
          submitting={submitting}
        />
      )}
    </div>
  );
}

function ExpandedBody({
  loading,
  notes,
  showLegacy,
  legacyNotes,
  draft,
  onDraftChange,
  onSubmit,
  isOnline,
  submitting,
}: {
  loading: boolean;
  notes: PosNote[];
  showLegacy: boolean;
  legacyNotes?: string;
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isOnline: boolean;
  submitting: boolean;
}) {
  return (
    <div className="px-3 pb-3 space-y-3">
      {loading && <p className="text-xs text-gray-500">Lade Notizen…</p>}
      {!loading && notes.length > 0 && <NoteList notes={notes} />}
      {!loading && showLegacy && legacyNotes && (
        <LegacyNote body={legacyNotes} />
      )}
      {!loading && !showLegacy && notes.length === 0 && (
        <p className="text-xs text-gray-500">Noch keine Notizen.</p>
      )}
      <AddNoteForm
        draft={draft}
        onDraftChange={onDraftChange}
        onSubmit={onSubmit}
        isOnline={isOnline}
        submitting={submitting}
      />
    </div>
  );
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function SummaryButton({
  expanded,
  label,
  onClick,
}: {
  expanded: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'w-full flex items-start gap-2 p-3 text-left ' +
        'active:bg-purple-100/80 transition-colors min-h-[48px]'
      }
    >
      <div className="flex-1 min-w-0">
        <div className="font-bold text-xs md:text-sm text-purple-800 mb-0.5">
          Notes
        </div>
        <p className="text-xs md:text-sm text-gray-600 line-clamp-2">{label}</p>
      </div>
      {expanded ? (
        <ChevronUp size={18} className="text-purple-600 flex-shrink-0 mt-0.5" />
      ) : (
        <ChevronDown
          size={18}
          className="text-purple-600 flex-shrink-0 mt-0.5"
        />
      )}
    </button>
  );
}

function NoteList({ notes }: { notes: PosNote[] }) {
  return (
    <ul className="space-y-2">
      {notes.map((note) => (
        <li
          key={note.id}
          className="bg-white rounded-lg border border-purple-100 p-2.5"
        >
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <span className="text-xs font-bold text-purple-800 truncate">
              {note.user_name || 'Unbekannt'}
            </span>
            <span className="text-[11px] text-gray-500 flex-shrink-0">
              {formatDateTime(note.created_at)}
            </span>
          </div>
          <p className="text-xs md:text-sm text-gray-700 whitespace-pre-wrap">
            {note.body}
          </p>
        </li>
      ))}
    </ul>
  );
}

function LegacyNote({ body }: { body: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-2.5">
      <p className="text-[11px] font-bold text-gray-500 mb-1">
        Ursprünglicher Hinweis
      </p>
      <p className="text-xs md:text-sm text-gray-600 whitespace-pre-wrap">
        {body}
      </p>
    </div>
  );
}

function AddNoteForm({
  draft,
  onDraftChange,
  onSubmit,
  isOnline,
  submitting,
}: {
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isOnline: boolean;
  submitting: boolean;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <textarea
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        placeholder="Fortschritt notieren…"
        rows={2}
        disabled={!isOnline || submitting}
        className={
          'w-full px-3 py-2.5 text-base rounded-xl border border-purple-200 ' +
          'bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 ' +
          'disabled:bg-gray-100 disabled:text-gray-400 resize-none'
        }
      />
      {!isOnline && (
        <p className="text-[11px] text-amber-700">
          Notizen nur online möglich.
        </p>
      )}
      <button
        type="submit"
        disabled={!isOnline || submitting || !draft.trim()}
        className={
          'w-full min-h-[48px] px-3 py-2.5 rounded-xl font-bold text-sm ' +
          'bg-purple-600 text-white hover:bg-purple-700 ' +
          'active:scale-[0.98] transition-all ' +
          'disabled:opacity-50 disabled:pointer-events-none ' +
          'flex items-center justify-center gap-2'
        }
      >
        <Plus size={16} />
        {submitting ? 'Speichern…' : 'Notiz hinzufügen'}
      </button>
    </form>
  );
}
